import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { StoreAuthorityScopeSnapshot, StoreAuthorityStoreSnapshot } from '../../../../shared/models';
import { AuthorityLevelEnum } from '../../enum/store-authority.enum';
import { compareTextAsc } from '../../utils/sort.util';

const SCOPE_LEVEL_COLUMNS = 2;
const STORE_LEVEL_COLUMNS = 3;

@Component({
  selector: 'app-store-authority-viewer',
  templateUrl: './store-authority-viewer.component.html',
  styleUrls: ['./store-authority-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreAuthorityViewerComponent {
  readonly level = input<string>(null);
  readonly levelLabel = input<string>('');
  readonly accessAllStores = input<boolean>(false);
  readonly scopeDetails = input<StoreAuthorityScopeSnapshot[]>([]);
  readonly storesView = input<StoreAuthorityStoreSnapshot[]>([]);

  readonly searchTerm = signal('');
  readonly selectedScopeCode = signal<string>(null);

  readonly isStoreLevel = computed(() => this.level() === AuthorityLevelEnum.STORE);
  readonly hasScopeList = computed(() => !this.accessAllStores() && !this.isStoreLevel());
  readonly columns = computed(() => (this.isStoreLevel() ? STORE_LEVEL_COLUMNS : SCOPE_LEVEL_COLUMNS));

  readonly sortedScopes = computed(() => [...this.scopeDetails()].sort((a, b) => compareTextAsc(a.code, b.code)));

  readonly selectedScope = computed(
    () => this.sortedScopes().find(scope => scope.code === this.selectedScopeCode()) || this.sortedScopes()[0]
  );

  readonly activeStores = computed(() =>
    this.isStoreLevel() ? this.storesView() : this.selectedScope()?.stores || []
  );

  readonly filteredStores = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.activeStores();
    }
    return this.activeStores().filter(
      store => store.code.toLowerCase().includes(term) || store.name.toLowerCase().includes(term)
    );
  });

  constructor() {
    effect(
      () => {
        const scopes = this.sortedScopes();
        if (scopes.length && !scopes.some(scope => scope.code === this.selectedScopeCode())) {
          this.selectedScopeCode.set(scopes[0].code);
        }
      },
      { allowSignalWrites: true }
    );
  }

  onSelectScope(scope: StoreAuthorityScopeSnapshot) {
    this.selectedScopeCode.set(scope.code);
    this.searchTerm.set('');
  }

  onSearchInput(value: string) {
    this.searchTerm.set(value);
  }

  clearSearch() {
    this.searchTerm.set('');
  }
}
